<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters } from 'dashboard/composables/store';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  calendar: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['delete']);

const { t } = useI18n();
const getters = useStoreGetters();

const drive = computed(() => {
  const drives = getters['drives/getDrives']?.value || [];
  return drives.find(d => d.id === props.calendar.settings?.drive_id) || {};
});

const providerIcon = computed(() => {
  const icons = {
    google: 'i-woot-google-drive',
    microsoft: 'i-woot-sharepoint',
    onedrive: 'i-woot-onedrive',
    sharepoint: 'i-woot-sharepoint',
    dropbox: 'i-woot-dropbox',
  };
  return icons[props.calendar.settings?.provider] || 'i-lucide-calendar';
});

const statusColor = computed(() => {
  if (props.calendar.status === 'active')
    return 'text-green-600 dark:text-green-400';
  if (props.calendar.status === 'error')
    return 'text-red-600 dark:text-red-400';
  return 'text-slate-600 dark:text-slate-400';
});

const statusText = computed(() => {
  const statusMap = {
    active: t('INTEGRATION_SETTINGS.CALENDARS.STATUS.ACTIVE'),
    inactive: t('INTEGRATION_SETTINGS.CALENDARS.STATUS.INACTIVE'),
    error: t('INTEGRATION_SETTINGS.CALENDARS.STATUS.ERROR'),
  };
  return statusMap[props.calendar.status] || props.calendar.status;
});

const handleDelete = () => {
  emit('delete', props.calendar.id);
};
</script>

<template>
  <div
    class="flex flex-col flex-1 p-6 m-[1px] outline outline-n-container outline-1 bg-n-alpha-3 rounded-md shadow"
  >
    <div class="flex items-start justify-between">
      <div class="p-3 rounded-lg bg-slate-50 dark:bg-slate-700 w-12 h-12 mb-4">
        <Icon
          :icon="providerIcon"
          class="size-6 text-slate-700 dark:text-slate-300"
        />
      </div>
      <span
        v-tooltip="statusText"
        class="text-white p-0.5 rounded-full w-5 h-5 flex items-center justify-center"
        :class="[calendar.status === 'active' ? 'bg-n-teal-9' : 'bg-n-slate-8']"
      >
        <i class="i-ph-check-bold text-sm" />
      </span>
    </div>

    <div class="flex flex-col flex-1">
      <h3 class="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
        {{ calendar.settings?.file_name }}
      </h3>
      <p class="text-sm text-slate-600 dark:text-slate-400 mb-6 flex-grow">
        {{ drive.name || drive.provider }}
      </p>
    </div>

    <div class="flex gap-2 justify-start">
      <Button ruby outline @click="handleDelete">
        {{ $t('INTEGRATION_SETTINGS.CALENDARS.DELETE.BUTTON') }}
      </Button>
    </div>
  </div>
</template>
